// EditorDoSite.tsx
//
// O editor de blocos da landing. Molde: EditorLayoutComanda — recebe o valor
// inicial, avisa quem chamou quando o dono manda salvar, e NÃO SABE SALVAR. Sem
// service, sem api, sem token: quem grava é a section.
//
// Os campos de cada bloco vêm do CATÁLOGO SERVIDO PELO BACKEND, não de uma cópia
// aqui. É o que impede o editor de oferecer um campo que o servidor recusa — o
// defeito herdado do FlyOps, onde o schema do bloco está escrito três vezes e a
// divergência produziu o bug "salvar não persiste".
//
// Mutação sempre imutável e por índice: `blocos.map((b, i) => i === indice ? ... : b)`.
// Editar o objeto no lugar não re-renderiza e o dono acha que o campo não salva.

import React, { useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import CampoDeImagem from "./CampoDeImagem";
import PreviewDoSite from "./PreviewDoSite";
import {
  BlocoDoSite,
  MarcaDaLanding,
  TemaDoSite,
  UnidadeDaLanding,
} from "../../../../comum/site";
import { DescritorServido } from "../../../../services/site.service";

export interface BlocoEditavel {
  tipo: string;
  config: Record<string, unknown>;
}

interface Props {
  marca: MarcaDaLanding;
  unidades: UnidadeDaLanding[];
  tema: TemaDoSite;
  catalogo: DescritorServido[];
  blocosIniciais: BlocoEditavel[];
  /** Verdadeiro quando o que está na tela é a landing padrão, ainda não gravada. */
  doPadrao: boolean;
  salvando: boolean;
  onMudarTema: (tema: TemaDoSite) => void;
  onSalvar: (blocos: BlocoEditavel[]) => void;
  /**
   * PUBLICAR RECEBE OS BLOCOS DA TELA, e isso é o conserto de um defeito que
   * MENTIA para o dono: antes o botão só avisava "publica aí", e a section
   * publicava o que estava no BANCO. Quem editava e clicava em Publicar sem
   * salvar lia "Publicado." e via o conteúdo ANTIGO no ar.
   */
  onPublicar: (blocos: BlocoEditavel[]) => void;
}

const CANTOS: TemaDoSite["cantos"][] = ["reto", "suave", "redondo"];
const TIPOGRAFIAS: { valor: TemaDoSite["tipografia"]; rotulo: string }[] = [
  { valor: "moderna", rotulo: "Moderna" },
  { valor: "classica", rotulo: "Clássica (serifada)" },
  { valor: "manuscrita", rotulo: "Descontraída" },
];

const EditorDoSite: React.FC<Props> = ({
  marca,
  unidades,
  tema,
  catalogo,
  blocosIniciais,
  doPadrao,
  salvando,
  onMudarTema,
  onSalvar,
  onPublicar,
}) => {
  const [blocos, setBlocos] = useState<BlocoEditavel[]>(blocosIniciais);
  const [aberto, setAberto] = useState<number | null>(null);
  const [tipoNovo, setTipoNovo] = useState<string>("");

  const disponiveis = catalogo.filter((descritor) =>
    descritor.escopos.includes(marca.escopo)
  );

  const mudarConfig = (indice: number, chave: string, valor: unknown) => {
    setBlocos((atuais) =>
      atuais.map((bloco, i) =>
        i === indice ? { ...bloco, config: { ...bloco.config, [chave]: valor } } : bloco
      )
    );
  };

  const mover = (indice: number, direcao: -1 | 1) => {
    const destino = indice + direcao;
    if (destino < 0 || destino >= blocos.length) return;
    setBlocos((atuais) => {
      const copia = [...atuais];
      [copia[indice], copia[destino]] = [copia[destino], copia[indice]];
      return copia;
    });
    setAberto(destino);
  };

  const remover = (indice: number) => {
    setBlocos((atuais) => atuais.filter((_, i) => i !== indice));
    setAberto(null);
  };

  const acrescentar = () => {
    const descritor = disponiveis.find((d) => d.tipo === tipoNovo);
    if (!descritor) return;
    setBlocos((atuais) => [...atuais, { tipo: descritor.tipo, config: { ...descritor.padrao } }]);
    setAberto(blocos.length);
    setTipoNovo("");
  };

  /** Um campo do editor, desenhado pelo TIPO que o descritor declara. */
  const desenharCampo = (
    indice: number,
    campo: any,
    valor: unknown
  ): React.ReactNode => {
    const rotuloEAjuda = (
      <>
        <span>{campo.rotulo}</span>
        {campo.ajuda && <AlfredoStyles.CampoAjuda>{campo.ajuda}</AlfredoStyles.CampoAjuda>}
      </>
    );

    switch (campo.tipo) {
      case "booleano":
        return (
          <AlfredoStyles.LinhaCaixa key={campo.chave}>
            <AlfredoStyles.Caixa
              type="checkbox"
              checked={valor === true}
              onChange={(e) => mudarConfig(indice, campo.chave, e.target.checked)}
            />
            <span>{campo.rotulo}</span>
          </AlfredoStyles.LinhaCaixa>
        );

      case "escolha":
        return (
          <AlfredoStyles.Campo key={campo.chave}>
            {rotuloEAjuda}
            <AlfredoStyles.Seletor
              value={typeof valor === "string" ? valor : ""}
              onChange={(e) => mudarConfig(indice, campo.chave, e.target.value)}
            >
              {(campo.opcoes ?? []).map((opcao: any) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </option>
              ))}
            </AlfredoStyles.Seletor>
          </AlfredoStyles.Campo>
        );

      case "imagem":
        return (
          <CampoDeImagem
            key={campo.chave}
            rotulo={campo.rotulo}
            ajuda={campo.ajuda}
            valor={typeof valor === "string" ? valor : ""}
            aoMudar={(url) => mudarConfig(indice, campo.chave, url)}
          />
        );

      case "textoLongo":
      case "html":
        return (
          <AlfredoStyles.Campo key={campo.chave}>
            {rotuloEAjuda}
            <AlfredoStyles.AreaTexto
              value={typeof valor === "string" ? valor : ""}
              onChange={(e) => mudarConfig(indice, campo.chave, e.target.value)}
            />
          </AlfredoStyles.Campo>
        );

      case "numero":
        return (
          <AlfredoStyles.Campo key={campo.chave}>
            {rotuloEAjuda}
            <AlfredoStyles.Entrada
              type="number"
              min={campo.minimo}
              max={campo.maximo}
              value={typeof valor === "number" ? valor : ""}
              onChange={(e) =>
                mudarConfig(
                  indice,
                  campo.chave,
                  e.target.value === "" ? 0 : Number(e.target.value)
                )
              }
            />
          </AlfredoStyles.Campo>
        );

      case "lista":
        return (
          <AlfredoStyles.Campo key={campo.chave} as="div">
            {rotuloEAjuda}
            {(Array.isArray(valor) ? valor : []).map((item: any, iItem: number) => (
              <div
                key={iItem}
                style={{
                  display: "grid",
                  gap: "var(--sp-6)",
                  padding: "var(--sp-10)",
                  marginTop: "var(--sp-6)",
                  border: "1px solid #e2e6e3",
                  borderRadius: 8,
                }}
              >
                {(campo.itens ?? []).map((subcampo: any) =>
                  subcampo.tipo === "escolha" ? (
                    <AlfredoStyles.Seletor
                      key={subcampo.chave}
                      value={item?.[subcampo.chave] ?? ""}
                      onChange={(e) => {
                        const novos = [...(valor as any[])];
                        novos[iItem] = { ...item, [subcampo.chave]: e.target.value };
                        mudarConfig(indice, campo.chave, novos);
                      }}
                    >
                      {(subcampo.opcoes ?? []).map((opcao: any) => (
                        <option key={opcao.valor} value={opcao.valor}>
                          {opcao.rotulo}
                        </option>
                      ))}
                    </AlfredoStyles.Seletor>
                  ) : subcampo.tipo === "imagem" ? (
                    // A galeria e as marcas guardam a foto DENTRO de uma lista,
                    // e por isso não passam pelo `case "imagem"` de cima. Sem
                    // esta linha, os dois blocos que mais pedem foto seriam
                    // justamente os que continuariam exigindo endereço colado.
                    <CampoDeImagem
                      key={subcampo.chave}
                      rotulo={subcampo.rotulo}
                      valor={item?.[subcampo.chave] ?? ""}
                      aoMudar={(url) => {
                        const novos = [...(valor as any[])];
                        novos[iItem] = { ...item, [subcampo.chave]: url };
                        mudarConfig(indice, campo.chave, novos);
                      }}
                    />
                  ) : (
                    <AlfredoStyles.Entrada
                      key={subcampo.chave}
                      placeholder={subcampo.rotulo}
                      value={item?.[subcampo.chave] ?? ""}
                      onChange={(e) => {
                        const novos = [...(valor as any[])];
                        novos[iItem] = { ...item, [subcampo.chave]: e.target.value };
                        mudarConfig(indice, campo.chave, novos);
                      }}
                    />
                  )
                )}
                <AlfredoStyles.Linha>
                  <AlfredoStyles.BotaoIcone
                    type="button"
                    title="Remover"
                    onClick={() =>
                      mudarConfig(
                        indice,
                        campo.chave,
                        (valor as any[]).filter((_, i) => i !== iItem)
                      )
                    }
                  >
                    <AlfredoStyles.MaterialIcons>delete</AlfredoStyles.MaterialIcons>
                  </AlfredoStyles.BotaoIcone>
                </AlfredoStyles.Linha>
              </div>
            ))}
            <AlfredoStyles.Linha style={{ marginTop: "var(--sp-8)" }}>
              <AlfredoStyles.BotaoIcone
                type="button"
                title="Acrescentar"
                disabled={
                  Array.isArray(valor) && campo.maximo ? valor.length >= campo.maximo : false
                }
                onClick={() => {
                  const vazio: Record<string, string> = {};
                  for (const subcampo of campo.itens ?? []) {
                    vazio[subcampo.chave] =
                      subcampo.tipo === "escolha" ? subcampo.opcoes?.[0]?.valor ?? "" : "";
                  }
                  mudarConfig(indice, campo.chave, [...(Array.isArray(valor) ? valor : []), vazio]);
                }}
              >
                <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
              </AlfredoStyles.BotaoIcone>
            </AlfredoStyles.Linha>
          </AlfredoStyles.Campo>
        );

      default:
        return (
          <AlfredoStyles.Campo key={campo.chave}>
            {rotuloEAjuda}
            <AlfredoStyles.Entrada
              value={typeof valor === "string" ? valor : ""}
              onChange={(e) => mudarConfig(indice, campo.chave, e.target.value)}
            />
          </AlfredoStyles.Campo>
        );
    }
  };

  // O preview quer BlocoDoSite (com uuid); o editor trabalha sem uuid, porque a
  // identidade de um bloco de rascunho é a POSIÇÃO — é ela que o dono arrasta.
  const blocosParaPreview: BlocoDoSite[] = blocos.map((bloco, indice) => ({
    blocoUuid: `editor-${indice}`,
    tipo: bloco.tipo,
    ordem: indice,
    config: bloco.config,
  }));

  return (
    <AlfredoStyles.ContainerEditor>
    <AlfredoStyles.ColunasEditor>
      <AlfredoStyles.ColunaEditor>
        <AlfredoStyles.Cartao>
          <AlfredoStyles.CartaoTitulo>Aparência</AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.CartaoSubtitulo>
            A cor da marca pinta capa, títulos e detalhes. O botão de pedir continua
            verde: o contraste dele é calibrado e é o que garante que o cliente
            enxergue a ação em qualquer tela.
          </AlfredoStyles.CartaoSubtitulo>

          <AlfredoStyles.Linha>
            <AlfredoStyles.Campo style={{ flex: "0 0 auto" }}>
              <span>Cor da marca</span>
              <AlfredoStyles.CampoCor
                type="color"
                value={tema.marca}
                onChange={(e) => onMudarTema({ ...tema, marca: e.target.value })}
              />
            </AlfredoStyles.Campo>

            <AlfredoStyles.Campo style={{ flex: "0 0 auto" }}>
              <span>Fundo da capa</span>
              <AlfredoStyles.CampoCor
                type="color"
                value={tema.marcaFundo}
                onChange={(e) => onMudarTema({ ...tema, marcaFundo: e.target.value })}
              />
            </AlfredoStyles.Campo>

            <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 150)" }}>
              <span>Texto sobre a cor</span>
              <AlfredoStyles.Seletor
                value={tema.marcaTexto}
                onChange={(e) =>
                  onMudarTema({ ...tema, marcaTexto: e.target.value as "claro" | "escuro" })
                }
              >
                <option value="claro">Claro</option>
                <option value="escuro">Escuro</option>
              </AlfredoStyles.Seletor>
            </AlfredoStyles.Campo>

            <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 130)" }}>
              <span>Cantos</span>
              <AlfredoStyles.Seletor
                value={tema.cantos}
                onChange={(e) =>
                  onMudarTema({ ...tema, cantos: e.target.value as TemaDoSite["cantos"] })
                }
              >
                {CANTOS.map((canto) => (
                  <option key={canto} value={canto}>
                    {canto}
                  </option>
                ))}
              </AlfredoStyles.Seletor>
            </AlfredoStyles.Campo>

            <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 180)" }}>
              <span>Tipografia</span>
              <AlfredoStyles.Seletor
                value={tema.tipografia}
                onChange={(e) =>
                  onMudarTema({
                    ...tema,
                    tipografia: e.target.value as TemaDoSite["tipografia"],
                  })
                }
              >
                {TIPOGRAFIAS.map((item) => (
                  <option key={item.valor} value={item.valor}>
                    {item.rotulo}
                  </option>
                ))}
              </AlfredoStyles.Seletor>
            </AlfredoStyles.Campo>
          </AlfredoStyles.Linha>
        </AlfredoStyles.Cartao>

        <AlfredoStyles.Cartao>
          <AlfredoStyles.CartaoTitulo>Blocos da página</AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.CartaoSubtitulo>
            {doPadrao
              ? "Esta é a página que o Alfredo montou com os dados da sua loja. " +
                "Ela já está no ar. Mexa à vontade — nada muda para o cliente até você publicar."
              : "Nada muda para o cliente até você clicar em Publicar."}
          </AlfredoStyles.CartaoSubtitulo>

          {blocos.map((bloco, indice) => {
            const descritor = catalogo.find((d) => d.tipo === bloco.tipo);
            const selecionado = aberto === indice;
            return (
              <AlfredoStyles.BlocoItem
                key={`${bloco.tipo}-${indice}`}
                $selecionado={selecionado}
                style={{ marginBottom: "var(--sp-8)" }}
              >
                <AlfredoStyles.BlocoCabecalho>
                  <AlfredoStyles.BlocoNome
                    type="button"
                    onClick={() => setAberto(selecionado ? null : indice)}
                  >
                    <AlfredoStyles.MaterialIcons>
                      {selecionado ? "expand_less" : "expand_more"}
                    </AlfredoStyles.MaterialIcons>
                    {descritor?.rotulo ?? bloco.tipo}
                  </AlfredoStyles.BlocoNome>

                  <AlfredoStyles.BlocoAcoes>
                    <AlfredoStyles.BotaoIcone
                      type="button"
                      title="Subir"
                      disabled={indice === 0}
                      onClick={() => mover(indice, -1)}
                    >
                      <AlfredoStyles.MaterialIcons>arrow_upward</AlfredoStyles.MaterialIcons>
                    </AlfredoStyles.BotaoIcone>
                    <AlfredoStyles.BotaoIcone
                      type="button"
                      title="Descer"
                      disabled={indice === blocos.length - 1}
                      onClick={() => mover(indice, 1)}
                    >
                      <AlfredoStyles.MaterialIcons>arrow_downward</AlfredoStyles.MaterialIcons>
                    </AlfredoStyles.BotaoIcone>
                    <AlfredoStyles.BotaoIcone
                      type="button"
                      title="Remover"
                      onClick={() => remover(indice)}
                    >
                      <AlfredoStyles.MaterialIcons>delete</AlfredoStyles.MaterialIcons>
                    </AlfredoStyles.BotaoIcone>
                  </AlfredoStyles.BlocoAcoes>
                </AlfredoStyles.BlocoCabecalho>

                {selecionado && (
                  <AlfredoStyles.BlocoCampos>
                    {descritor?.descricao && (
                      <AlfredoStyles.CampoAjuda>{descritor.descricao}</AlfredoStyles.CampoAjuda>
                    )}
                    {(descritor?.campos ?? []).map((campo: any) =>
                      desenharCampo(indice, campo, bloco.config[campo.chave])
                    )}
                    {(descritor?.campos ?? []).length === 0 && (
                      <AlfredoStyles.CampoAjuda>
                        Este bloco monta o conteúdo sozinho, a partir do cadastro das
                        suas lojas. Não há nada para preencher aqui.
                      </AlfredoStyles.CampoAjuda>
                    )}
                  </AlfredoStyles.BlocoCampos>
                )}
              </AlfredoStyles.BlocoItem>
            );
          })}

          <AlfredoStyles.Linha style={{ marginTop: "var(--sp-16)" }}>
            <AlfredoStyles.Seletor
              value={tipoNovo}
              onChange={(e) => setTipoNovo(e.target.value)}
              style={{ maxWidth: "calc(var(--u) * 260)" }}
            >
              <option value="">Acrescentar um bloco…</option>
              {disponiveis.map((descritor) => {
                const usados = blocos.filter((b) => b.tipo === descritor.tipo).length;
                return (
                  <option
                    key={descritor.tipo}
                    value={descritor.tipo}
                    disabled={usados >= descritor.maximoPorPagina}
                  >
                    {descritor.rotulo}
                    {usados >= descritor.maximoPorPagina ? " (já está na página)" : ""}
                  </option>
                );
              })}
            </AlfredoStyles.Seletor>
            <AlfredoStyles.Button type="button" disabled={!tipoNovo} onClick={acrescentar}>
              Acrescentar
            </AlfredoStyles.Button>
          </AlfredoStyles.Linha>
        </AlfredoStyles.Cartao>

        <AlfredoStyles.Linha>
          <AlfredoStyles.Button
            type="button"
            disabled={salvando}
            onClick={() => onSalvar(blocos)}
          >
            {salvando ? "Salvando…" : "Salvar rascunho"}
          </AlfredoStyles.Button>
          <AlfredoStyles.Button
            type="button"
            color="#26792f"
            disabled={salvando || blocos.length === 0}
            onClick={() => onPublicar(blocos)}
          >
            Publicar no site
          </AlfredoStyles.Button>
        </AlfredoStyles.Linha>
      </AlfredoStyles.ColunaEditor>

      <AlfredoStyles.ColunaPreview>
        <AlfredoStyles.MolduraPreview>
          <AlfredoStyles.BarraPreview>
            <AlfredoStyles.MaterialIcons>visibility</AlfredoStyles.MaterialIcons>
            Como fica no site
          </AlfredoStyles.BarraPreview>
          <AlfredoStyles.TelaPreview>
            <PreviewDoSite
              tema={tema}
              marca={marca}
              unidades={unidades}
              blocos={blocosParaPreview}
            />
          </AlfredoStyles.TelaPreview>
        </AlfredoStyles.MolduraPreview>
      </AlfredoStyles.ColunaPreview>
    </AlfredoStyles.ColunasEditor>
    </AlfredoStyles.ContainerEditor>
  );
};

export default EditorDoSite;
